/**
 * bridge mode 時に「サムネクリック→親に open 委譲」を実現するための React Context
 *
 * embedded.tsx が bridge mode を検出したら、React tree を BridgeProvider でラップする。
 * 各 Player コンポーネント（PopupPlayer / SliderPlayer 等）は useBridge() で
 * bridge の有無を確認し、有効なら sendOpenIntent を呼ぶことでモーダル展開を親に委譲する。
 *
 * Provider が無い（=bridge mode 無効、direct/inline-fallback）場合は useBridge() が null を返す。
 * 既存の direct mode 動作を一切壊さないための null-safe 設計。
 */

import { createContext, useContext, type FC, type ReactNode } from 'react'

import type {
  PublicTenant,
  ScenarioWithNodesAndVideoUrls,
} from '../../../orval/generated/public/model'

/**
 * 案C: SWR cache 共有
 *
 * 子（iframe 内）で SWR に解決済みの scenario / tenant を、親への open 委譲時に
 * postMessage で同梱して親側 SWR cache を pre-populate するためのデータ。
 * EmbeddingAiInterview が SWR 解決後に setSharedData() で登録し、
 * BridgeProvider 側のクロージャに保持する。
 */
export type BridgeSharedData = {
  scenario: ScenarioWithNodesAndVideoUrls
  tenant: PublicTenant | null
}

export type BridgeContextValue = {
  /** サムネクリック時に親に open 要求を送る */
  sendOpenIntent: (videoId?: string) => void
  /**
   * 案C: 親に postMessage で同梱する scenario / tenant を登録する。
   * EmbeddingAiInterview が useSWR 解決のタイミングで呼ぶ。
   * 旧 BridgeProvider（未対応）からは省略されるため optional。
   */
  setSharedData?: (data: BridgeSharedData) => void
}

const BridgeContext = createContext<BridgeContextValue | null>(null)

export type BridgeProviderProps = {
  value: BridgeContextValue
  children: ReactNode
}

export const BridgeProvider: FC<BridgeProviderProps> = ({ value, children }) => (
  <BridgeContext.Provider value={value}>{children}</BridgeContext.Provider>
)

/**
 * bridge mode が有効なら BridgeContextValue を、無効なら null を返す。
 * 利用側は null チェックで bridge の有無を判定する。
 */
export const useBridge = (): BridgeContextValue | null => useContext(BridgeContext)
